<template>
  <div class="container-fluid py-4">
    <!-- Template Header -->
    <div class="row mb-4">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0">
          <div class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <div class="bg-primary-light p-3 rounded-2 me-3">
                <vue-feather type="file-text" stroke="#652aa3" size="32"></vue-feather>
              </div>
              <div>
                <h4 class="mb-1 fw-bold">{{ template.name }}</h4>
                <p class="text-muted mb-1">{{ template.description || 'No description' }}</p>
                <div class="d-flex align-items-center gap-2">
                  <span v-if="template.is_default" class="badge bg-success">Default</span>
                  <span class="badge bg-secondary">{{ formatType(template.type) }}</span>
                  <span class="badge bg-primary">Weight: {{ template.weight }}</span>
                </div>
              </div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <button v-if="profile.role !== 'user'" class="btn btn-outline-primary" @click="editTemplate">
                <vue-feather type="edit" size="14" class="me-1"></vue-feather>
                Edit Template
              </button>
              <button class="btn btn-outline-dark" @click="$router.push('/review-templates')">
                <vue-feather type="arrow-left" size="16" class="me-1"></vue-feather>
                Back
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>

    <div class="row">
      <!-- Criteria List -->
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="list" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Review Criteria ({{ criteriaCount }})
            </div>
            <div class="d-flex gap-2">
              <UInput v-model="search" size="md" placeholder="Search criteria" class="mb-0 search-input"/>
              <button v-if="profile.role !== 'user'" class="btn btn-primary" @click="openCreateCriteria">
                <vue-feather type="plus" size="18" class="me-1"></vue-feather>
                Add Criteria
              </button>
            </div>
          </div>

          <!-- Loading Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Sections with Criteria -->
          <div v-else-if="!loading && criteriaCount > 0">
            <!-- Display by Sections -->
            <div v-for="(section, sIndex) in sectionsWithCriteria" :key="sIndex" class="mb-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 class="mb-0 fw-semibold">{{ section.title }}</h6>
                  <small class="text-muted">{{ section.description }}</small>
                </div>
                <span class="badge bg-light text-dark">{{ section.criteria.length }} criteria</span>
              </div>

              <div v-if="section.criteria.length > 0" class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead class="table-light">
                    <tr>
                      <th class="fw-semibold">Question</th>
                      <th class="fw-semibold">Answer Type</th>
                      <th class="fw-semibold">Max Score</th>
                      <th class="fw-semibold">Weight</th>
                      <th class="fw-semibold">Order</th>
                      <th v-if="profile.role !== 'user'" class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="criteria in section.criteria" :key="criteria.id">
                      <td>
                        <div class="fw-semibold">{{ criteria.question }}</div>
                        <small v-if="criteria.description" class="text-muted">{{ criteria.description }}</small>
                      </td>
                      <td>
                        <span class="badge bg-secondary">{{ criteria.answer_type }}</span>
                      </td>
                      <td>{{ criteria.max_score ?? '-' }}</td>
                      <td><span class="badge bg-light text-dark">{{ criteria.weight ?? '-' }}</span></td>
                      <td>{{ criteria.order ?? '-' }}</td>
                      <td v-if="profile.role !== 'user'" class="text-end">
                        <button class="btn btn-sm btn-outline-primary me-2" @click="openEditCriteria(criteria)">
                          <vue-feather type="edit" size="14"></vue-feather>
                        </button>
                        <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteCriteria(criteria)">
                          <vue-feather type="trash" size="14"></vue-feather>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else-if="!loading && criteriaCount === 0" class="text-center py-5">
            <vue-feather type="list" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Review Criteria Found</h5>
            <p class="text-muted">Create your first criterion to get started.</p>
            <button class="btn btn-primary" @click="openCreateCriteria">
              <vue-feather type="plus" size="16" class="me-1"></vue-feather>
              Add Criteria
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Add/Edit Criteria Modal -->
    <AddEditCriteriaModal
      :open="showCriteriaModal"
      :is-editing="isEditingCriteria"
      :form-data="criteriaForm"
      :templates="[template]"
      :sections="template.sections || []"
      :saving="saving"
      @close="closeCriteriaModal"
      @save="saveCriteria"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { decrypt } from '@/utils/crypto'
import notify from '@/utils/notify'
import confirmDialog from '@/utils/confirmDialog'
import reviewTemplateService from '@/api/reviewTemplateService'
import reviewCriteriaService from '@/api/reviewCriteriaService'
import AddEditCriteriaModal from '@/components/modals/AddEditCriteriaModal.vue'
import VueFeather from 'vue-feather'

export default {
  name: 'ReviewTemplateDetails',
  components: { AddEditCriteriaModal, VueFeather },
  setup() {
    const route = useRoute()
    const router = useRouter()

    const loading = ref(true)
    const saving = ref(false)
    const search = ref('')
    const template = ref({
      id: null,
      name: '',
      description: '',
      type: 'core',
      weight: 1,
      is_default: false,
      sections: [],
      criteria: []
    })

    const profile = ref({})

    // Criteria Modal State
    const showCriteriaModal = ref(false)
    const isEditingCriteria = ref(false)
    const criteriaForm = ref({
      review_template_id: '',
      section: '',
      question: '',
      description: '',
      answer_type: 'rating',
      options: [],
      max_score: null,
      weight: null,
      order: null
    })

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
      return new Date(dateString).toLocaleDateString(undefined, options)
    }

    const formatType = (type) => {
      if (!type) return 'Core'
      return type.charAt(0).toUpperCase() + type.slice(1)
    }

    const criteriaCount = computed(() => {
      return (template.value.criteria || []).length
    })

    const filteredCriteria = computed(() => {
      const criteria = template.value.criteria || []
      if (!search.value) return criteria

      const s = search.value.toLowerCase()
      return criteria.filter(c =>
        (c.question || '').toLowerCase().includes(s) ||
        (c.description || '').toLowerCase().includes(s) ||
        (c.answer_type || '').toLowerCase().includes(s) ||
        (c.section || '').toLowerCase().includes(s)
      )
    })

    const sectionsWithCriteria = computed(() => {
      const sections = template.value.sections || []
      const criteria = filteredCriteria.value

      // Create sections with their criteria
      const result = sections.map(section => ({
        ...section,
        criteria: criteria.filter(c => c.section === section.title)
      }))

      // Add uncategorized section if there are criteria without a section
      const uncategorized = criteria.filter(c => !c.section || c.section === null)
      if (uncategorized.length > 0) {
        result.push({
          title: 'Uncategorized',
          description: 'Criteria not assigned to any section',
          criteria: uncategorized
        })
      }

      // Filter out sections with no criteria
      return result.filter(s => s.criteria.length > 0)
    })

    const fetchTemplate = async () => {
      try {
        loading.value = true
        const response = await reviewTemplateService.getTemplate(route.params.id)

        console.log('API Response:', response)

        // Handle different response structures
        let data = response
        if (response.data) {
          data = response.data
        }

        template.value = {
          id: data.id || null,
          name: data.name || '',
          description: data.description || '',
          type: data.type || 'core',
          weight: data.weight || 1,
          is_default: data.is_default || false,
          sections: Array.isArray(data.sections) ? data.sections : [],
          criteria: Array.isArray(data.criteria) ? data.criteria : [],
          created_at: data.created_at,
          updated_at: data.updated_at
        }

      } catch (error) {
        notify.alert.error('Failed to fetch template details')
        console.error('Fetch error:', error)
      } finally {
        loading.value = false
      }
    }

    const openCreateCriteria = () => {
      isEditingCriteria.value = false
      criteriaForm.value = {
        review_template_id: template.value.id,
        section: '',
        question: '',
        description: '',
        answer_type: 'rating',
        options: [],
        max_score: null,
        weight: null,
        order: null
      }
      showCriteriaModal.value = true
    }

    const openEditCriteria = (criteria) => {
      isEditingCriteria.value = true
      criteriaForm.value = {
        id: criteria.id,
        review_template_id: template.value.id,
        section: criteria.section || '',
        question: criteria.question || '',
        description: criteria.description || '',
        answer_type: criteria.answer_type || 'rating',
        options: Array.isArray(criteria.options) ? [...criteria.options] : [],
        max_score: criteria.max_score ?? null,
        weight: criteria.weight ?? null,
        order: criteria.order ?? null
      }
      showCriteriaModal.value = true
    }

    const closeCriteriaModal = () => {
      if (!saving.value) {
        showCriteriaModal.value = false
        isEditingCriteria.value = false
        criteriaForm.value = {
          review_template_id: template.value.id,
          section: '',
          question: '',
          description: '',
          answer_type: 'rating',
          options: [],
          max_score: null,
          weight: null,
          order: null
        }
      }
    }

    const saveCriteria = async (criteriaData) => {
      try {
        saving.value = true

        if (isEditingCriteria.value) {
          await reviewCriteriaService.update(criteriaData.id, criteriaData)
        } else {
          await reviewCriteriaService.create(criteriaData)
        }

        await fetchTemplate()
        closeCriteriaModal()
      } catch (error) {
        console.error('Error saving criteria:', error)
        notify.alert.error('Failed to save criteria')
      } finally {
        saving.value = false
      }
    }

    const confirmDeleteCriteria = (criteria) => {
      confirmDialog('Delete Criteria', async () => {
        try {
          saving.value = true
          await reviewCriteriaService.destroy(criteria.id)
          await fetchTemplate()
        } catch (error) {
          console.error('Error deleting criteria:', error)
          notify.alert.error('Failed to delete criteria')
        } finally {
          saving.value = false
        }
      })
    }

    const editTemplate = () => {
      router.push('/review-templates')
    }

    onMounted(async () => {
      try {
        const profileData = sessionStorage.getItem('profile')
        if (profileData) {
          profile.value = JSON.parse(decrypt(profileData))
        }
        await fetchTemplate()
      } catch (error) {
        console.error('Error initializing component:', error)
      }
    })

    return {
      loading,
      saving,
      search,
      template,
      profile,
      criteriaCount,
      filteredCriteria,
      sectionsWithCriteria,
      showCriteriaModal,
      isEditingCriteria,
      criteriaForm,
      formatDate,
      formatType,
      openCreateCriteria,
      openEditCriteria,
      closeCriteriaModal,
      saveCriteria,
      confirmDeleteCriteria,
      editTemplate
    }
  }
}
</script>

<style scoped>
.bg-primary-light {
  background-color: rgba(101, 42, 163, 0.1);
}

.search-input {
  min-width: 260px;
}

.font-size-125 {
  font-size: 1.25rem;
}

.color-gray-600 {
  color: #6c757d;
}
</style>
